import {
  INFO_PALETTE,
  isDefined,
  surfaceMode,
  themeVarName,
} from '@mfw/common';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'error'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface ContentTypeColors {
    nutritionPlan: string;
    exercise: string;
    program: string;
    workout: string;
    meal: string;
  }
  /**
   * The second brand anchor, as its own slot rather than a `PaletteColor`.
   *
   * `PaletteColor` is augmented above with a REQUIRED `lighter`/`darker`, and
   * the accent ramp has neither — the catalog derives exactly four steps from
   * `accent-main`, and `theme-derivation.spec.ts` already pins that shape. So
   * this is declared for what it is instead of being padded out with two
   * invented values, and the one override that reads `lighter`
   * (`chip.tsx`'s avatar) asks for it through a step accessor.
   */
  interface AccentColor {
    contrastText: string;
    light: string;
    main: string;
    dark: string;
  }
  interface Palette {
    contentType: ContentTypeColors;
    accent: AccentColor;
  }
  interface PaletteOptions {
    contentType?: ContentTypeColors;
    accent?: AccentColor;
  }
}

// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

// Generated from the THEME_VARS catalog (@mfw/common) — the MUI JS palette is
// built once at startup and cannot read CSS vars, so these literals are a copy.
// `theme-derivation.spec.ts` asserts they match; regenerate rather than hand-edit.
export const primary = {
  contrastText: '#000000',
  dark: '#00a953',
  darker: '#007538',
  light: '#6bea93',
  lighter: '#acf7be',
  main: '#28d771',
};

export const accent = {
  contrastText: '#000000',
  dark: '#0080bc',
  light: '#5fbffd',
  main: '#27a9f1',
};

export const secondary = {
  contrastText: '#FFFFFF',
  dark: '#5119B7',
  darker: '#27097A',
  light: '#C684FF',
  lighter: '#EFD6FF',
  main: '#8E33FF',
};

// The literals live in `@mfw/common` (`INFO_PALETTE`, with the note on why its
// `contrastText` is black), where the editor's readability warnings measure the
// same steps this paints.
export const info = INFO_PALETTE;

export const success = {
  contrastText: '#ffffff',
  dark: '#118D57',
  darker: '#065E49',
  light: '#77ED8B',
  lighter: '#D3FCD2',
  main: '#22C55E',
};

export const warning = {
  contrastText: grey[800],
  dark: '#B76E00',
  darker: '#7A4100',
  light: '#FFD666',
  lighter: '#FFF5CC',
  main: '#FFAB00',
};

export const error = {
  contrastText: '#FFFFFF',
  dark: '#B71D18',
  darker: '#7A0916',
  light: '#FFAC82',
  lighter: '#FFE9D5',
  main: '#FF5630',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const contentType = {
  exercise: '#10B981',
  meal: '#14B8A6',
  nutritionPlan: '#F59E0B',
  program: '#6366F1',
  workout: '#F43F5E',
};

export const action = {
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  disabledOpacity: 0.48,
  focus: alpha(grey[500], 0.24),
  hover: alpha(grey[500], 0.08),
  hoverOpacity: 0.08,
  selected: alpha(grey[500], 0.16),
};

const base = {
  action,
  common,
  contentType,
  divider: alpha(grey[500], 0.2),
  error,
  grey,
  info,
  primary,
  secondary,
  success,
  warning,
};

// ----------------------------------------------------------------------

/**
 * A resolved `--mfw-palette-*` record (defaults ← tenant ← org), as
 * `AppThemeProvider` computes it. Typed loosely on purpose: this module must
 * not depend on the context that owns the provider.
 */
type ResolvedThemeVars = Record<string, string>;

/** Only concrete colours may reach MUI — it parses them (contrast text, `alpha()`). */
const CONCRETE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*\d)/i;

const pick = (
  vars: ResolvedThemeVars | undefined,
  varName: string,
  fallback: string,
) => {
  const value = vars?.[varName];
  return isDefined(value) && CONCRETE_COLOR.test(value.trim())
    ? value
    : fallback;
};

/**
 * The MUI JS palette, built FROM the resolved theme vars.
 *
 * It used to be a frozen copy of the catalog defaults, which made it a second
 * source of truth that could not follow a rebrand: `theme.palette.*` answered
 * for the platform default no matter which tenant was on screen, `mode` was
 * permanently `'dark'` (so every `mode === 'light'` branch in the component
 * overrides was unreachable), and `alpha(theme.palette.<slot>, a)` mixed the
 * wrong base colour. The CSS vars said one thing and the JS palette another.
 *
 * `vars` is optional so the module-level exports above still work for the
 * non-provider callers (`shadows`, `custom-shadows`, `presets`) and so a call
 * with no resolved record yet falls back to exactly the previous values.
 *
 * `info` and `grey` have no catalog vars — they are not rebrandable — so they
 * stay literal. So do `divider` and the `action` opacities, whose catalog
 * entries hold `var()` references and bare channels rather than colours.
 */
export function palette(vars?: ResolvedThemeVars) {
  // Named objects rather than inline members: the catalog-tracking spec reads
  // this file's source to check every FALLBACK still equals its catalog var,
  // and it needs the literal to sit next to the key it belongs to.
  const background = {
    default: pick(vars, themeVarName.background.default, '#070f09'),
    neutral: pick(vars, themeVarName.background.neutral, '#1f2821'),
    paper: pick(vars, themeVarName.background.paper, '#121a13'),
  };

  const text = {
    disabled: pick(vars, themeVarName.text.disabled, grey[600]),
    primary: pick(vars, themeVarName.text.primary, '#FFFFFF'),
    // Fallback mirrors the catalog default, which is now an alpha step of
    // text-primary rather than the old fixed '#aab7c4' — see the catalog entry.
    secondary: pick(
      vars,
      themeVarName.text.secondary,
      'rgba(255, 255, 255, 0.68)',
    ),
  };

  const fromVars = <T extends Record<string, string>>(
    group: T,
    mapping: Partial<Record<keyof T, string>>,
  ) =>
    Object.fromEntries(
      Object.entries(group).map(([key, fallback]) => {
        const varName = mapping[key as keyof T];
        return [
          key,
          isDefined(varName) ? pick(vars, varName, fallback) : fallback,
        ];
      }),
    ) as T;

  return {
    ...base,
    // The second brand anchor, in the JS palette for the first time. It reached
    // `:root` from the day it was added and was still unspendable: `base` below
    // listed every other family, so `sx={{ color: 'accent.main' }}` resolved to
    // an unknown path, which MUI passes through as a CSS colour and the browser
    // drops — a silent no-op. Its only consumer was one stylesheet.
    accent: fromVars(accent, {
      contrastText: themeVarName.accent.contrastText,
      dark: themeVarName.accent.dark,
      light: themeVarName.accent.light,
      main: themeVarName.accent.main,
    }),
    action: {
      ...base.action,
      active: pick(vars, themeVarName.action.active, grey[500]),
    },
    background,
    error: fromVars(error, {
      contrastText: themeVarName.error.contrastText,
      dark: themeVarName.error.dark,
      darker: themeVarName.error.darker,
      light: themeVarName.error.light,
      lighter: themeVarName.error.lighter,
      main: themeVarName.error.main,
    }),
    mode: surfaceMode(background.default),
    primary: fromVars(primary, {
      contrastText: themeVarName.primary.contrastText,
      dark: themeVarName.primary.dark,
      darker: themeVarName.primary.darker,
      light: themeVarName.primary.light,
      lighter: themeVarName.primary.lighter,
      main: themeVarName.primary.main,
    }),
    secondary: fromVars(secondary, {
      dark: themeVarName.secondary.dark,
      darker: themeVarName.secondary.darker,
      light: themeVarName.secondary.light,
      lighter: themeVarName.secondary.lighter,
      main: themeVarName.secondary.main,
    }),
    success: fromVars(success, {
      contrastText: themeVarName.success.contrastText,
      dark: themeVarName.success.dark,
      darker: themeVarName.success.darker,
      light: themeVarName.success.light,
      lighter: themeVarName.success.lighter,
      main: themeVarName.success.main,
    }),
    text,
    warning: fromVars(warning, {
      contrastText: themeVarName.warning.contrastText,
      dark: themeVarName.warning.dark,
      darker: themeVarName.warning.darker,
      light: themeVarName.warning.light,
      lighter: themeVarName.warning.lighter,
      main: themeVarName.warning.main,
    }),
  };
}
